import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import {
  bills,
  forecast,
  payments,
  subscriptions,
  type BillUploadResult,
  type ForecastItem,
  type PaymentEvent,
} from '../api/client';
import { ForecastDrawer } from '../components/ForecastDrawer';
import { PaymentDrawer } from '../components/PaymentDrawer';
import { BillDropzone } from '../components/BillDropzone';
import { OcrBadge } from '../components/BillViewer';
import { Button, EmptyState, ErrorState, Input, Modal, Select, Spinner } from '../components/ui';
import { buildMatrix, isTwoStep, monthWindow, monthWindowEnding, upcomingMonths, type MatrixRow } from '../lib/matrix';
import {
  actualMonthsBySubscription,
  buildForecastRows,
  forecastBySubscription,
  forecastPerCurrency,
  forecastRowRub,
  forecastRubByMonth,
  forecastTotals,
  lastKnownFxRates,
  windowReachesForecast,
  type ForecastRow,
} from '../lib/forecast';
import { FORECAST_RATE_CURRENCIES, useForecastRates } from '../lib/use-forecast-rates';
import { useForecastToggle } from '../lib/use-forecast-toggle';
import { useHiddenForecasts } from '../lib/use-hidden-forecasts';
import { formatAmount, formatCompact, formatDate, formatMonthKey, formatRenewal } from '../lib/format';
import { toInt, useQueryParams } from '../lib/query-params';

const WINDOW_OPTIONS = [
  { value: '1', label: 'Last month' },
  { value: '3', label: 'Last 3 months' },
  { value: '6', label: 'Last 6 months' },
  { value: '12', label: 'Last 12 months' },
  { value: '18', label: 'Last 18 months' },
  { value: '24', label: 'Last 24 months' },
] as const;

/** A reimbursement window, not an audit one: three months is what gets asked for. */
const MONTHS_DEFAULT = 3;
/** Future months the "Ending with" select offers, so a window can look ahead. */
const MONTHS_AHEAD = 6;
const PAGE_LIMIT = 200;

/**
 * What "Register payment now" hands the create form: the forecast's own amount
 * and renewal date, so the drawer's offer is one confirm away — never a guess
 * the user has to retype.
 */
interface PaymentPrefill {
  subscriptionId: string;
  amount: number;
  currency: string;
  occurredAt: string;
}

/**
 * Rows = subscriptions (grouped by vendor group), columns = months, cells =
 * payment chips. Clicking a chip opens the payment event drawer.
 */
export function PaymentsPage() {
  const { params, setParams } = useQueryParams();
  const navigate = useNavigate();
  const [eventId, setEventId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  /** The forecast pill that opened the forecast drawer, by subscription id. */
  const [forecastSubId, setForecastSubId] = useState<string | null>(null);
  /** The create form, prefilled from "Register payment now". */
  const [prefill, setPrefill] = useState<PaymentPrefill | null>(null);

  const monthsCount = toInt(params.months) ?? MONTHS_DEFAULT;
  const anchor = params.anchor; // `YYYY-MM`, lets a user scroll back without losing today

  const months = useMemo(
    () => (anchor ? monthWindowEnding(monthsCount, anchor) : monthWindow(monthsCount)),
    [anchor, monthsCount],
  );

  const first = months[0] ?? monthWindow(MONTHS_DEFAULT)[0] ?? '';
  const last = months[months.length - 1] ?? first;
  const from = `${first}-01`;
  const to = last
    ? new Date(Date.UTC(Number(last.slice(0, 4)), Number(last.slice(5, 7)), 0, 23, 59, 59)).toISOString()
    : undefined;

  const subscriptionList = useQuery({
    queryKey: ['subscriptions', 'matrix', PAGE_LIMIT],
    queryFn: ({ signal }) => subscriptions.list({ page: 1, limit: PAGE_LIMIT }, signal),
  });

  const eventList = useQuery({
    queryKey: ['payments', 'matrix', from, to],
    queryFn: ({ signal }) => payments.list({ from, to, page: 1, limit: PAGE_LIMIT }, signal),
    placeholderData: keepPreviousData,
  });

  /**
   * The forecast only has something to say when the window reaches the current
   * month or beyond, and the checkbox is the user's word against that default.
   */
  const forecastRelevant = windowReachesForecast(months);
  const [forecastShown, setForecastShown] = useForecastToggle(forecastRelevant);
  const showForecast = forecastRelevant && forecastShown;

  const forecastQuery = useQuery({
    queryKey: ['forecast', from, to],
    queryFn: ({ signal }) => forecast.list({ from, to }, signal),
    enabled: showForecast,
  });

  const matrix = useMemo(
    () => buildMatrix(subscriptionList.data?.items ?? [], eventList.data?.items ?? [], months),
    [subscriptionList.data, eventList.data, months],
  );

  /**
   * Dotted pills *inside* the subscription rows they belong to. The suppression
   * against the loaded pills is re-done here (not only in the API) because the
   * matrix — not the forecast query — owns what "actual" means on this screen.
   */
  const forecastRows = useMemo(() => {
    if (!showForecast) return [];
    return buildForecastRows(
      forecastQuery.data?.items ?? [],
      months,
      actualMonthsBySubscription(eventList.data?.items ?? [], months),
    );
  }, [showForecast, forecastQuery.data, months, eventList.data]);

  /** Subscriptions whose forecasts the user switched off (per browser). */
  const [hiddenForecasts, toggleHiddenForecast, showAllForecasts] = useHiddenForecasts();

  /**
   * ₽ for the forecast: the toolbar's rate inputs win, the freshest rate already
   * on screen is the default, and a currency left with neither shows "—". No
   * rate is ever invented here.
   */
  const lastKnownRates = useMemo(() => lastKnownFxRates(eventList.data?.items ?? []), [eventList.data]);
  const { rates: forecastRates, inputs: rateInputs, setRate } = useForecastRates(lastKnownRates);

  /** The visible forecast rows: what the user has not switched off. */
  const visibleForecastRows = useMemo(
    () => forecastRows.filter((row) => !hiddenForecasts.has(row.item.subscriptionId)),
    [forecastRows, hiddenForecasts],
  );

  /** `subscriptionId` -> its forecast cells, so a row can draw its own pills. */
  const forecastBySub = useMemo(() => forecastBySubscription(visibleForecastRows), [visibleForecastRows]);

  /** The footer's grand total, over the rows still on screen. */
  const forecastSummary = useMemo(
    () => forecastTotals(forecastPerCurrency(visibleForecastRows), forecastRates),
    [visibleForecastRows, forecastRates],
  );

  /** ≈₽ per month for the footer's "incl. forecast" row. */
  const forecastByMonth = useMemo(
    () => forecastRubByMonth(visibleForecastRows, months, forecastRates),
    [visibleForecastRows, months, forecastRates],
  );

  const forecastItem = useMemo(
    () => visibleForecastRows.find((row) => row.item.subscriptionId === forecastSubId)?.item ?? null,
    [visibleForecastRows, forecastSubId],
  );

  const groups = [...matrix.groups, ...(matrix.ungrouped.length > 0 ? [{ name: 'No group', rows: matrix.ungrouped }] : [])];
  const loading = subscriptionList.isPending || eventList.isPending;
  const error = subscriptionList.isError ? subscriptionList.error : eventList.isError ? eventList.error : null;

  /** Matrix cell -> the reimbursement report for exactly that month. */
  const openMonthReport = (monthKey: string) => {
    const [year, month] = monthKey.split('-').map(Number);
    if (!year || !month) return;
    const from = `${monthKey}-01`;
    const to = new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
    void navigate(`/reports?from=${from}&to=${to}`);
  };

  // The matrix fetches one page; a window with more events than that silently
  // lost rows, so the truncation is now stated instead of implied.
  const fetchedEvents = eventList.data?.items.length ?? 0;
  const totalEvents = eventList.data?.meta.total ?? 0;
  const truncated = totalEvents > fetchedEvents;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Payments matrix</h1>
          <p className="page-header__sub">
            {months.length} months · {formatMonthKey(first)} → {formatMonthKey(last)} · {matrix.totals.eventsCount} payments
          </p>
        </div>
        <div className="page-header__actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            Record payment
          </Button>
          <Button variant="secondary" onClick={() => setUploadOpen(true)}>
            Upload bill
          </Button>
        </div>
      </div>

      <div className="toolbar">
        <div className="toolbar__field">
          <label className="toolbar__label" htmlFor="matrix-window">Window</label>
          <Select
            id="matrix-window"
            value={String(monthsCount)}
            onChange={(event) => setParams({ months: event.target.value === String(MONTHS_DEFAULT) ? undefined : event.target.value })}
          >
            {WINDOW_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
        <div className="toolbar__field">
          <label className="toolbar__label" htmlFor="matrix-anchor">Ending with</label>
          <Select id="matrix-anchor" value={anchor ?? ''} onChange={(event) => setParams({ anchor: event.target.value || undefined })}>
            <option value="">Current month</option>
            {/* Newest first: the months still ahead of us (what the forecast is
                for), then the audit trail backwards. */}
            {upcomingMonths(MONTHS_AHEAD)
              .slice()
              .reverse()
              .map((key) => (
                <option key={key} value={key}>
                  {formatMonthKey(key)}
                </option>
              ))}
            {monthWindow(36)
              .slice()
              .reverse()
              .map((key) => (
                <option key={key} value={key}>
                  {formatMonthKey(key)}
                </option>
              ))}
          </Select>
        </div>
        <div className="toolbar__spacer" />
        <div className="toolbar__field toolbar__check">
          <input
            id="matrix-forecast"
            type="checkbox"
            checked={showForecast}
            disabled={!forecastRelevant}
            onChange={(event) => setForecastShown(event.target.checked)}
          />
          <label className="toolbar__label" htmlFor="matrix-forecast">Show forecasted values</label>
        </div>
        {showForecast ? (
          <div className="toolbar__field toolbar__rates" role="group" aria-label="Forecast exchange rates">
            <span className="toolbar__label">≈₽ rate</span>
            <div className="toolbar__rates-inputs">
              {FORECAST_RATE_CURRENCIES.map((currency) => (
                <label className="rate-input" key={currency} htmlFor={`rate-${currency}`}>
                  <span className="rate-input__code">{currency}→₽</span>
                  <Input
                    id={`rate-${currency}`}
                    className="rate-input__field"
                    type="number"
                    inputMode="decimal"
                    step="any"
                    min="0"
                    placeholder="—"
                    value={rateInputs[currency] ?? ''}
                    onChange={(event) => setRate(currency, event.target.value)}
                  />
                </label>
              ))}
            </div>
          </div>
        ) : null}
        {showForecast && hiddenForecasts.size > 0 ? (
          <div className="toolbar__field toolbar__check">
            <span className="toolbar__label">
              Forecasts hidden for {hiddenForecasts.size} subscription{hiddenForecasts.size === 1 ? '' : 's'}
            </span>
            <button type="button" className="link-btn" onClick={showAllForecasts}>
              show again
            </button>
          </div>
        ) : null}
        <div className="kpi" style={{ minWidth: 220, padding: 'var(--space-2) var(--space-4)' }}>
          <div className="kpi__num" style={{ fontSize: 18 }}>{formatCompact(matrix.totals.rubTotal, 'RUB')}</div>
          <div className="kpi__label">
            {Object.entries(matrix.totals.paidByCurrency)
              .map(([currency, amount]) => formatAmount(amount, currency))
              .join(' + ') || 'no payments'} in this window
          </div>
        </div>
      </div>

      <div className="card card--flush">
        {loading ? (
          <Spinner label="Building the matrix" />
        ) : error ? (
          <ErrorState message={error.message} onRetry={() => void eventList.refetch()} />
        ) : groups.length === 0 ? (
          <EmptyState title="No subscriptions yet" hint="Create a subscription to start recording payments." />
        ) : (
          <div className="matrix-wrap">
            <table className="matrix">
              <thead>
                <tr>
                  <th scope="col" className="matrix__name-col">Subscription</th>
                  {months.map((key) => (
                    <th scope="col" key={key} className="matrix__cell">
                      <button
                        type="button"
                        className="matrix__month-btn"
                        title={`Open the report for ${formatMonthKey(key)}`}
                        onClick={() => openMonthReport(key)}
                      >
                        {formatMonthKey(key)}
                      </button>
                    </th>
                  ))}
                  <th scope="col" className="matrix__total">Total</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <MatrixGroupRows
                    key={group.name}
                    name={group.name}
                    rows={group.rows}
                    months={months}
                    forecastBySub={forecastBySub}
                    rates={forecastRates}
                    onOpen={setEventId}
                    onOpenForecast={setForecastSubId}
                  />
                ))}
              </tbody>
              {showForecast && visibleForecastRows.length > 0 ? (
                <tfoot className="matrix-tfoot--forecast">
                  <tr>
                    <th scope="row" className="matrix__name-col">
                      incl. forecast
                      <span className="matrix__rub">renewals assumed till Dec 2026 · dotted = not booked</span>
                    </th>
                    {forecastByMonth.map((cell, index) => (
                      <td className="matrix__total" key={months[index] ?? index}>
                        {cell.kind === 'rub' ? (
                          `≈ ${formatCompact(cell.value, 'RUB')}`
                        ) : (
                          <span className="cell-muted">{cell.kind === 'no-rate' ? '—' : '·'}</span>
                        )}
                      </td>
                    ))}
                    <td className="matrix__total">
                      {Object.entries(forecastSummary.perCurrency)
                        .map(([currency, amount]) => formatAmount(amount, currency))
                        .join(' + ') || '—'}
                      <span className="matrix__rub">
                        {forecastSummary.rubTotal !== null
                          ? `≈ ${formatCompact(forecastSummary.rubTotal, 'RUB')}`
                          : 'no ₽ rate yet'}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              ) : null}
              <tfoot>
                <tr>
                  <th scope="row" className="matrix__name-col">All subscriptions</th>
                  {months.map((key) => (
                    <td className="matrix__total" key={key}>
                      {matrix.totals.byMonth[key] ? formatCompact(matrix.totals.byMonth[key], 'RUB') : <span className="cell-muted">—</span>}
                    </td>
                  ))}
                  <td className="matrix__total matrix__grand">
                    {formatCompact(matrix.totals.rubTotal, 'RUB')}
                    <span className="matrix__rub">
                      {Object.entries(matrix.totals.paidByCurrency)
                        .map(([currency, amount]) => formatAmount(amount, currency))
                        .join(' + ') || '—'}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {truncated ? (
        <p className="matrix__truncated" role="note">
          Showing the first {fetchedEvents} of {totalEvents} payments in this window — the totals below
          cover only what is loaded.{' '}
          <button type="button" className="link-btn" onClick={() => setParams({ months: '6' })}>
            Narrow the window
          </button>
          {' '}or{' '}
          <button type="button" className="link-btn" onClick={() => openMonthReport(months[months.length - 1] ?? '')}>
            open the report
          </button>{' '}
          for the exact figures.
        </p>
      ) : null}

      <PaymentDrawer
        eventId={eventId}
        onClose={() => setEventId(null)}
        createOpen={createOpen}
        onCreateClose={() => {
          setCreateOpen(false);
          setPrefill(null);
        }}
        onCreated={(id) => setEventId(id)}
        createPrefill={prefill}
      />

      {forecastItem ? (
        <ForecastDrawer
          item={forecastItem}
          onClose={() => setForecastSubId(null)}
          hidden={hiddenForecasts.has(forecastItem.subscriptionId)}
          onRegisterPayment={() => {
            const { next } = forecastItem;
            if (!next) return;
            setPrefill({
              subscriptionId: forecastItem.subscriptionId,
              amount: next.amount,
              currency: next.currency,
              occurredAt: next.date,
            });
            setForecastSubId(null);
            setCreateOpen(true);
          }}
          onOpenSubscription={() => {
            const name = forecastItem.name;
            setForecastSubId(null);
            void navigate(`/subscriptions?q=${encodeURIComponent(name)}`);
          }}
          onHide={() => {
            toggleHiddenForecast(forecastItem.subscriptionId);
            setForecastSubId(null);
          }}
        />
      ) : null}

      <Modal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        title="Upload a bill"
        description="The OCR pipeline fills the payment draft; confirm it in the drawer."
        footer={
          <Button variant="secondary" onClick={() => setUploadOpen(false)}>
            Close
          </Button>
        }
      >
        <UploadBillForm />
      </Modal>
    </>
  );
}

/** Global upload: pick the payment, then drop the file and watch the OCR state. */
function UploadBillForm() {
  const [selectedId, setSelectedId] = useState('');
  const [uploaded, setUploaded] = useState<BillUploadResult | null>(null);
  const subscriptionList = useQuery({
    queryKey: ['subscriptions', 'matrix', PAGE_LIMIT],
    queryFn: ({ signal }) => subscriptions.list({ page: 1, limit: PAGE_LIMIT }, signal),
  });

  const selected = subscriptionList.data?.items.find((item) => item.id === selectedId);

  const eventList = useQuery({
    queryKey: ['payments', 'pick', selectedId],
    queryFn: ({ signal }) => payments.list({ subscriptionId: selectedId, page: 1, limit: 50 }, signal),
    enabled: Boolean(selectedId),
  });

  return (
    <div>
      <p className="state__hint" style={{ marginTop: 0 }}>
        Pick the subscription and the payment event the bill belongs to, then drop the file.
      </p>
      <div className="toolbar" style={{ marginBottom: 'var(--space-3)' }}>
        <div className="toolbar__field" style={{ flex: 1, minWidth: 220 }}>
          <label className="toolbar__label" htmlFor="upload-sub">Subscription</label>
          <Select
            id="upload-sub"
            value={selectedId}
            onChange={(event) => setSelectedId(event.target.value)}
          >
            <option value="">— choose —</option>
            {(subscriptionList.data?.items ?? []).map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="toolbar__field" style={{ flex: 1, minWidth: 220 }}>
          <label className="toolbar__label" htmlFor="upload-event">Payment event</label>
          <PaymentSelect events={eventList.data?.items ?? []} />
        </div>
      </div>
      {selected && eventList.data?.items[0] ? (
        <p className="state__hint">
          The file will be attached to the payment from {formatDate(eventList.data.items[0].occurredAt)}.
        </p>
      ) : null}
      {/* No payment selected -> no transaction to attach to, and the api would
          still store the file as an orphan no screen can reach. */}
      {selected && eventList.data?.items[0] ? (
        <BillDropzone
          transactionId={eventList.data.items[0].transactions[0]?.id}
          onUploaded={setUploaded}
        />
      ) : (
        <div className="dropzone dropzone--disabled" aria-disabled="true">
          <div className="dropzone__big">Pick a subscription with at least one payment</div>
          <div>The bill has to attach to a payment event.</div>
        </div>
      )}
      <UploadProgress billId={uploaded?.id ?? null} initialStatus={uploaded?.ocrStatus ?? null} />
    </div>
  );
}

/**
 * Polls the OCR status of the freshly uploaded bill (PENDING -> PROCESSING ->
 * EXTRACTED -> CONFIRMED/FAILED) until the pipeline settles.
 */
function UploadProgress({
  billId,
  initialStatus,
}: {
  billId: string | null;
  initialStatus: BillUploadResult['ocrStatus'];
}) {
  const query = useQuery({
    queryKey: ['bills', billId, 'upload'],
    queryFn: ({ signal }) => bills.get(billId ?? '', signal),
    enabled: Boolean(billId),
    refetchInterval: (current) => {
      const status = current.state.data?.ocrStatus;
      return status === 'PENDING' || status === 'PROCESSING' ? 1_500 : false;
    },
  });

  if (!billId) return null;
  const status = query.data?.ocrStatus ?? initialStatus;
  return (
    <p className="ocr-state" style={{ marginTop: 'var(--space-3)' }}>
      OCR pipeline:&nbsp;
      <OcrBadge status={status} error={query.data?.ocrError} />
      {query.data?.ocrStatus === 'EXTRACTED' ? (
        <span className="cell-muted"> — review it in the payment drawer, then confirm.</span>
      ) : null}
    </p>
  );
}

function PaymentSelect({ events }: { events: PaymentEvent[] }) {
  const first = events[0];
  if (!first) return <Select id="upload-event" disabled value="">
    <option value="">no payments recorded</option>
  </Select>;
  return (
    <Select id="upload-event" defaultValue={first.id}>
      {events.map((event) => (
        <option key={event.id} value={event.id}>
          {formatDate(event.occurredAt)} — {formatAmount(event.paidAmount, event.paidCurrency)}
        </option>
      ))}
    </Select>
  );
}

function MatrixGroupRows({
  name,
  rows,
  months,
  forecastBySub,
  rates,
  onOpen,
  onOpenForecast,
}: {
  name: string;
  rows: MatrixRow[];
  months: string[];
  /** `subscriptionId` -> that row's forecast cells (empty when the toggle is off). */
  forecastBySub: Map<string, ForecastRow>;
  rates: Record<string, number>;
  onOpen: (eventId: string) => void;
  onOpenForecast: (subscriptionId: string) => void;
}) {
  return (
    <>
      <tr className="group-row">
        <th scope="colgroup" colSpan={months.length + 2}>
          {name}
          <span className="matrix__rub"> {rows.length} subscription{rows.length === 1 ? '' : 's'}</span>
        </th>
      </tr>
      {rows.map((row) => {
        const status = row.subscription.effectiveStatus;
        const tone = status === 'EXPIRED' ? 'matrix-row--expired' : status === 'BLOCKED' ? 'matrix-row--blocked' : '';
        const forecast = forecastBySub.get(row.subscription.id);
        const forecastRubValue = forecast ? forecastRowRub(forecast, rates) : null;
        return (
          <tr key={row.subscription.id} className={tone}>
            <td className="matrix__name-col">
              <span className="matrix__sub-name">{row.subscription.name}</span>
              <span className="matrix__sub-meta">
                {status} · {formatRenewal(row.subscription.renewalCost, row.subscription.currency, row.subscription.renewalPeriod)}
              </span>
            </td>
            {months.map((key) => {
              const cell = row.cells.get(key) ?? [];
              const forecastAmount = forecast?.cells.get(key);
              // Actual wins: a month with a real pill never draws an assumption.
              if (cell.length === 0 && forecastAmount === undefined) {
                return (
                  <td className="matrix__cell" key={key}>
                    <span className="cell-muted">·</span>
                  </td>
                );
              }
              return (
                <td className="matrix__cell" key={key}>
                  {cell.map(({ event }) => (
                    <PaymentChip key={event.id} event={event} onOpen={onOpen} />
                  ))}
                  {forecastAmount !== undefined ? (
                    <ForecastChip
                      item={forecast!.item}
                      amount={forecastAmount}
                      month={key}
                      onOpen={() => onOpenForecast(forecast!.item.subscriptionId)}
                    />
                  ) : null}
                </td>
              );
            })}
            <td className="matrix__total">
              {Object.entries(row.paidByCurrency)
                .map(([currency, amount]) => formatAmount(amount, currency))
                .join(' + ') || <span className="cell-muted">—</span>}
              {row.rubTotal > 0 ? <span className="matrix__rub">{formatCompact(row.rubTotal, 'RUB')}</span> : null}
              {forecast && forecastRubValue !== null ? (
                <span className="matrix__rub matrix__rub--forecast" title="Forecasted renewals in this window">
                  ≈ {formatCompact(forecastRubValue, 'RUB')}
                </span>
              ) : null}
            </td>
          </tr>
        );
      })}
    </>
  );
}

/**
 * A forecast pill, now sitting in the subscription's own row: same box as a
 * real payment, dotted and muted, and clickable — what it opens is the
 * forecast drawer, not a payment that does not exist.
 */
function ForecastChip({
  item,
  amount,
  month,
  onOpen,
}: {
  item: ForecastItem;
  amount: number;
  month: string;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      className="pay-chip pay-chip--forecast"
      onClick={onOpen}
      title={`forecast · ${formatAmount(item.amount, item.currency)} · ${formatMonthKey(month)} · open for details`}
    >
      <span className="pay-chip__icon" aria-hidden="true">≈</span>
      {formatCompact(amount, item.currency)}
    </button>
  );
}

function PaymentChip({ event, onOpen }: { event: PaymentEvent; onOpen: (eventId: string) => void }) {
  const twoStep = isTwoStep(event);
  const icon = twoStep ? '⇄' : event.transactions[0]?.type === 'TOPUP' ? '＋' : '→';
  return (
    <button
      type="button"
      className={`pay-chip${twoStep ? ' pay-chip--two-step' : ''}${event.rubTotal === null ? ' pay-chip--no-rub' : ''}`}
      onClick={() => onOpen(event.id)}
      title={`${formatDate(event.occurredAt)} · ${event.flow}${event.rubTotal === null ? ' · no ₽ yet' : ` · ${formatAmount(event.rubTotal, 'RUB')}`}`}
    >
      <span className="pay-chip__icon" aria-hidden="true">
        {icon}
      </span>
      {formatCompact(event.paidAmount, event.paidCurrency)}
    </button>
  );
}
